/**
 * APT380 (client: "verify the Companies House character limits/restrictions applicable to company names and address fields and implement
 * the relevant validations where required"). The limits a registered company or LLP's details must meet to be filed at Companies House;
 * the sources are in docs/meeting-2026-11-02/apt380_ch_limits.md.
 * - Company name: at most 160 characters including spaces and the name ending (Company, Limited Liability Partnership and Business
 *   (Names and Trading Disclosures) Regulations 2015, SI 2015/17; Companies House incorporation rules).
 * - Registered office address (Companies House registrar's rules, form AD01): each address line, the town and the county are free text of
 *   at most 50 characters; the postcode at most 15.
 * A sole trader or partnership is not registered, so none of this applies to it.
 */
export const COMPANIES_HOUSE_NAME_MAX = 160;
export const COMPANIES_HOUSE_ADDRESS_LINE_MAX = 50;
export const COMPANIES_HOUSE_POSTCODE_MAX = 15;

/** The Company Info fields that are the company's registered name and registered office. */
export type RegisteredField = 'registeredName' | 'registeredOffice' | 'registeredOffice2' | 'city' | 'county' | 'postCode';

const REGISTERED_LIMITS: Record<RegisteredField, { max: number; label: string }> = {
  registeredName: { max: COMPANIES_HOUSE_NAME_MAX, label: 'Company name' },
  registeredOffice: { max: COMPANIES_HOUSE_ADDRESS_LINE_MAX, label: 'Registered office address line 1' },
  registeredOffice2: { max: COMPANIES_HOUSE_ADDRESS_LINE_MAX, label: 'Registered office address line 2' },
  city: { max: COMPANIES_HOUSE_ADDRESS_LINE_MAX, label: 'Town' },
  county: { max: COMPANIES_HOUSE_ADDRESS_LINE_MAX, label: 'County' },
  postCode: { max: COMPANIES_HOUSE_POSTCODE_MAX, label: 'Postcode' },
};

/** The most characters a registered-entity field accepts, or undefined for a field Companies House does not limit. */
export function companiesHouseMaxLength(key: string): number | undefined {
  return (REGISTERED_LIMITS as Record<string, { max: number } | undefined>)[key]?.max;
}

/**
 * The first registered field the user has CHANGED that is over its Companies House limit (a value saved earlier that is already longer is
 * left alone until it is edited, so an old record is never made impossible to save), as the sentence to show; null when none is.
 */
export function companiesHouseLimitProblem(
  current: Partial<Record<RegisteredField, string | null | undefined>>,
  saved: Partial<Record<RegisteredField, string | null | undefined>>,
): string | null {
  for (const key of Object.keys(REGISTERED_LIMITS) as RegisteredField[]) {
    const value = (current[key] ?? '').trim();
    if (value === (saved[key] ?? '').trim()) continue;
    const { max, label } = REGISTERED_LIMITS[key];
    if (value.length > max) {
      return `${label} can be at most ${max} characters for Companies House (it is ${value.length}).`;
    }
  }
  return null;
}
